// Onboarding finish handoff: decide where a freshly-onboarded user lands.
//
// The wizard's last step asks "What site do you want to review?" — so on Finish
// we take a *reachable* site straight into in-app team review (the lowest-friction
// path to a first capture, which is the strongest day-0 return signal). Only
// local / private hosts the review proxy can't reach fall back to the extension.
//
// Kept as a side-effect-free orchestrator with injected deps so it can be
// unit-tested without rendering OnboardingPage (mirrors lib/pendingExtensionOpen).
import { isLocalUrl } from './utils';
import { onboardingWebsiteError } from './onboardingWebsite';
import { URL_OWN_APP_REVIEW_ERROR } from '@overlayqa/shared';
import type { PendingExtensionOpen } from './pendingExtensionOpen';

/** Everything onboarding knows at Finish, mirroring what handleFinish builds. */
export interface OnboardingFinishInfo {
  url: string;
  urlType: string;
  projectId?: string;
  projectName?: string;
  projectSlug?: string;
  teamId?: string;
}

export type OnboardingDestination = 'team_review' | 'extension_open' | 'extension_pending' | 'dashboard';

/**
 * A URL is reviewable in-app when the server-side review proxy can fetch it.
 * Local / private / .local hosts are excluded (only the extension can reach
 * them). A project id is required because team review is created per project.
 */
export function canUseTeamReview(info: OnboardingFinishInfo): boolean {
  if (!info.projectId) return false;
  const url = info.url.trim();
  if (!url || onboardingWebsiteError(url)) return false;
  return !isLocalUrl(url);
}

/**
 * Build the team-review URL with the same query params LiveReviewModal uses so
 * the review page renders the mode indicator correctly.
 * Mirrors LiveReviewModal.tsx onSuccess.
 *
 * `authHandoff` (the server's short-TTL `?s=` token) is what makes the team UI
 * render: the review page is served from the API origin where the dashboard's
 * Clerk cookie may not reach (prod app.->api. split), so without it the render
 * falls back to the guest/CLIENT bundle. Omitted when the server didn't return
 * one (older server) — then the page relies on the legacy cookie path.
 *
 * `onboarding` appends `&onboarding=1`, which the review page reads into
 * `__REVIEW_CONFIG__.isOnboarding` to show the first-capture checklist. Only the
 * onboarding-finish path sets it; a review opened later renders without it.
 */
export function buildTeamReviewUrl(
  shareUrl: string,
  liveAvailable: boolean,
  hasExtension: boolean,
  authHandoff?: string,
  onboarding = false,
): string {
  const separator = shareUrl.includes('?') ? '&' : '?';
  const extParam = hasExtension ? '&ext=1' : '';
  const handoffParam = authHandoff ? `&s=${encodeURIComponent(authHandoff)}` : '';
  const onboardingParam = onboarding ? '&onboarding=1' : '';
  return `${shareUrl}${separator}liveAvailable=${liveAvailable}${extParam}${handoffParam}${onboardingParam}`;
}

/**
 * Server pre-flight verdict for the URL (POST /api/review-preflight). A
 * login-walled or unreachable site routes to the extension path instead of a
 * broken review canvas (TODOS #73, the samaretstp same-day churn). Absent
 * (probe failed, timed out, or skipped) fails OPEN to team review — the worst
 * case is the pre-probe behavior.
 */
export interface ReviewPreflightSummary {
  reviewable: boolean;
  reason: string;
}

export interface OnboardingFinishOptions {
  preflight?: ReviewPreflightSummary;
}

export interface OnboardingFinishDeps {
  /** Whether the extension content script is present on this page. */
  hasExtension: boolean;
  /** Explain and record why team review could not open; project setup has succeeded. */
  reportReviewFailure: (error: unknown) => void;
  /** Create a team review for the project's URL (clientReviewsApi.createLiveReview). */
  createLiveReview: (input: { projectId: string; pageUrl: string }) => Promise<{ shareUrl: string; liveAvailable: boolean; authHandoff?: string }>;
  /** Cross-origin navigation to the review page (window.location.assign). */
  navigateToReview: (url: string) => void;
  /** Tell the installed extension to open the site (OVERLAYQA_OPEN_SITE). */
  openSiteViaExtension: (info: OnboardingFinishInfo) => void;
  /** Stash the pending open so the dashboard resumes it once the extension installs. */
  writePending: (info: PendingExtensionOpen) => void;
  /** SPA navigate to the dashboard (where the extension paths land). */
  navigateToDashboard: () => void;
}

/**
 * Decide where an onboarding finish lands, and execute it.
 *
 * Reachable URL → create a team review and navigate there. If creation fails or
 * yields no share URL, fall through to the extension path so the user never
 * dead-ends. Local / private URL (or missing project) → existing extension path.
 *
 * Returns the destination taken (for analytics).
 */
export async function runOnboardingFinish(
  info: OnboardingFinishInfo,
  deps: OnboardingFinishDeps,
  options: OnboardingFinishOptions = {},
): Promise<OnboardingDestination> {
  // An own-app refusal is never a reason to open the same bad target in the extension.
  if (onboardingWebsiteError(info.url) || options.preflight?.reason === 'own-app') {
    deps.reportReviewFailure(Object.assign(new Error(URL_OWN_APP_REVIEW_ERROR), { code: 'OWN_APP_URL' }));
    deps.navigateToDashboard();
    return 'dashboard';
  }
  // The proxy can't render this site (login wall / unreachable from our
  // servers). The extension in the user's own signed-in browser can, so take
  // the extension path directly instead of creating a review that shows a
  // broken canvas. Only an explicit false diverts; absent verdicts fail open.
  if (canUseTeamReview(info) && options.preflight?.reviewable === false) {
    return runExtensionPath(info, deps);
  }
  if (canUseTeamReview(info) && info.projectId) {
    try {
      const review = await deps.createLiveReview({ projectId: info.projectId, pageUrl: info.url });
      if (!review.shareUrl) throw new Error('no share url');
      // This is the onboarding-finish path by definition → flag the review so it
      // renders the first-capture checklist (onboarding=1 → isOnboarding).
      deps.navigateToReview(buildTeamReviewUrl(review.shareUrl, review.liveAvailable, deps.hasExtension, review.authHandoff, true));
      return 'team_review';
    } catch (error) {
      deps.reportReviewFailure(error);
    }
  }
  return runExtensionPath(info, deps);
}

function runExtensionPath(info: OnboardingFinishInfo, deps: OnboardingFinishDeps): OnboardingDestination {
  if (deps.hasExtension) {
    deps.openSiteViaExtension(info);
    deps.navigateToDashboard();
    return 'extension_open';
  }
  deps.writePending(info);
  deps.navigateToDashboard();
  return 'extension_pending';
}
